if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.initSideZoom();
        this.initShareButton();
        this.initMobileFadeSlider();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.elements.thumbnails.classList.contains('ec-thumbnails-overlay') && this.elements.thumbnails.slider) {
          const itemSize = this.elements.thumbnails.slider.querySelector('.thumbnail-list__item')?.offsetHeight || 115;
          const step = itemSize + 8;
          const list = this.elements.thumbnails.slider;
          const prevBtn = this.elements.thumbnails.querySelector('button[name="previous"]');
          const nextBtn = this.elements.thumbnails.querySelector('button[name="next"]');
          const updateBtnVisibility = () => {
            requestAnimationFrame(() => {
              prevBtn?.removeAttribute('disabled');
              nextBtn?.removeAttribute('disabled');
              const atTop = list.scrollTop <= 2;
              const atBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 2;
              atTop ? prevBtn?.setAttribute('data-hidden', '') : prevBtn?.removeAttribute('data-hidden');
              atBottom ? nextBtn?.setAttribute('data-hidden', '') : nextBtn?.removeAttribute('data-hidden');
            });
          };
          prevBtn?.addEventListener('click', (e) => {
            e.stopImmediatePropagation();
            list.scrollBy({ top: -step, behavior: 'smooth' });
          }, { capture: true });
          nextBtn?.addEventListener('click', (e) => {
            e.stopImmediatePropagation();
            list.scrollBy({ top: step, behavior: 'smooth' });
          }, { capture: true });
          list.addEventListener('scroll', updateBtnVisibility);
          new MutationObserver(updateBtnVisibility).observe(this.elements.thumbnails, { attributes: true, subtree: true, attributeFilter: ['disabled'] });
          updateBtnVisibility();
        }
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      initMobileFadeSlider() {
        if (this.mql.matches) return;
        if (this.closest('.product')?.classList.contains('product--mobile-columns')) return;

        const viewer = this.elements.viewer;
        if (!viewer || !viewer.slider) return;

        const list = viewer.slider;
        const items = Array.from(list.querySelectorAll('[data-media-id]'));
        if (items.length < 2) return;

        const prevBtn = viewer.querySelector('button[name="previous"]');
        const nextBtn = viewer.querySelector('button[name="next"]');
        const currentEl = viewer.querySelector('.slider-counter--current');
        const totalEl = viewer.querySelector('.slider-counter--total');
        if (totalEl) totalEl.textContent = items.length;

        const getIndex = () => Math.max(0, items.findIndex((item) => item.classList.contains('is-active')));

        const updateControls = () => {
          const index = getIndex();
          if (currentEl) currentEl.textContent = index + 1;
          prevBtn?.toggleAttribute('disabled', index === 0);
          nextBtn?.toggleAttribute('disabled', index === items.length - 1);
        };

        const goToStep = (step) => {
          const nextIndex = getIndex() + step;
          if (nextIndex < 0 || nextIndex > items.length - 1) return;
          this.setActiveMedia(items[nextIndex].dataset.mediaId, false);
          updateControls();
        };

        [[prevBtn, -1], [nextBtn, 1]].forEach(([button, step]) => {
          if (!button) return;
          button.addEventListener(
            'click',
            (event) => {
              event.preventDefault();
              event.stopImmediatePropagation();
              goToStep(step);
            },
            { capture: true }
          );
        });

        let touchStartX = null;
        list.addEventListener('touchstart', (event) => {
          touchStartX = event.touches[0].clientX;
        }, { passive: true });
        list.addEventListener('touchend', (event) => {
          if (touchStartX === null) return;
          const deltaX = event.changedTouches[0].clientX - touchStartX;
          touchStartX = null;
          if (Math.abs(deltaX) < 40) return;
          goToStep(deltaX < 0 ? 1 : -1);
        }, { passive: true });

        updateControls();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        const isVertical = this.elements.thumbnails.classList.contains('ec-thumbnails-overlay')
				if (isVertical && this.elements.thumbnails.slider) {
					if (
						this.elements.thumbnails.slider.scrollHeight -
							this.elements.thumbnails.slider.clientHeight <=
						10
					)
						return
					this.elements.thumbnails.slider.scrollTo({
						top: thumbnail.offsetTop - 10,
						behavior: 'smooth'
					})
				} else if (!isVertical && this.elements.thumbnails.isSlideVisible(thumbnail, 10)) {
					return
				} else if (this.elements.thumbnails.slider) {
					this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft })
				}
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      initSideZoom() {
        this.zoomPanel = this.querySelector('.ec-zoom-side-panel');
        this.zoomLens  = this.querySelector('.ec-zoom-side-panel__lens');
        this.zoomImg   = this.querySelector('.ec-zoom-side-panel__img');
        if (!this.zoomPanel || !this.zoomLens || !this.zoomImg || !this.elements.viewer) return;

        const ZOOM = 2.5;
        const GAP  = 16;
        let currentSrc = null;
        let rafId = null;
        let zoomActive = false;

        const positionPanel = (gr) => {
          this.zoomPanel.style.left   = (gr.right + GAP) + 'px';
          this.zoomPanel.style.top    = gr.top + 'px';
          this.zoomPanel.style.width  = gr.width + 'px';
          this.zoomPanel.style.height = gr.height + 'px';
        };

        const onMouseMove = (e) => {
          if (!zoomActive) return;
          if (rafId) return;
          rafId = requestAnimationFrame(() => { rafId = null; });

          const activeSlide = this.querySelector('.product__media-item.is-active');
          if (!activeSlide) return;

          const zoomSrc = activeSlide.dataset.zoomSrc;
          if (!zoomSrc) return;

          const hitEl    = activeSlide.querySelector('.product-media-container') || activeSlide;
          const slideRect  = hitEl.getBoundingClientRect();
          const galleryRect = this.getBoundingClientRect();

          const xRaw = e.clientX - slideRect.left;
          const yRaw = e.clientY - slideRect.top;

          if (xRaw < 0 || yRaw < 0 || xRaw > slideRect.width || yRaw > slideRect.height) {
            onMouseLeave();
            return;
          }

          positionPanel(galleryRect);

          const panelW = galleryRect.width;
          const panelH = galleryRect.height;
          const imgW   = ZOOM * slideRect.width;
          const imgH   = ZOOM * slideRect.height;

          const rawLeft = panelW / 2 - xRaw * ZOOM;
          const rawTop  = panelH / 2 - yRaw * ZOOM;
          const imgLeft = Math.min(0, Math.max(panelW - imgW, rawLeft));
          const imgTop  = Math.min(0, Math.max(panelH - imgH, rawTop));

          if (currentSrc !== zoomSrc) {
            currentSrc = zoomSrc;
            this.zoomImg.src = zoomSrc;
          }
          this.zoomImg.style.width  = imgW + 'px';
          this.zoomImg.style.height = imgH + 'px';
          this.zoomImg.style.left   = imgLeft + 'px';
          this.zoomImg.style.top    = imgTop + 'px';

          const lensW      = slideRect.width / ZOOM;
          const lensH      = slideRect.height / ZOOM;
          const lensLocalX = Math.min(Math.max(xRaw - lensW / 2, 0), slideRect.width - lensW);
          const lensLocalY = Math.min(Math.max(yRaw - lensH / 2, 0), slideRect.height - lensH);

          this.zoomLens.style.width  = lensW + 'px';
          this.zoomLens.style.height = lensH + 'px';
          this.zoomLens.style.left   = (slideRect.left - galleryRect.left + lensLocalX) + 'px';
          this.zoomLens.style.top    = (slideRect.top  - galleryRect.top  + lensLocalY) + 'px';

          this.zoomPanel.classList.add('ec-zoom-side-panel--active');
          this.zoomLens.classList.add('ec-zoom-side-panel__lens--active');
        };

        const onMouseLeave = () => {
          this.zoomPanel.classList.remove('ec-zoom-side-panel--active');
          this.zoomLens.classList.remove('ec-zoom-side-panel__lens--active');
        };

        const deactivateZoom = () => {
          zoomActive = false;
          currentSrc = null;
          sliderList.classList.remove('ec-zoom-side-panel--engaged');
          onMouseLeave();
        };

        const onClick = (e) => {
          const activeSlide = this.querySelector('.product__media-item.is-active');
          if (!activeSlide || !activeSlide.dataset.zoomSrc) return;

          if (zoomActive) {
            deactivateZoom();
            return;
          }

          zoomActive = true;
          sliderList.classList.add('ec-zoom-side-panel--engaged');
          onMouseMove(e);
        };

        const sliderList = this.elements.viewer.querySelector('.product__media-list') || this.elements.viewer;
        sliderList.addEventListener('click', onClick);
        sliderList.addEventListener('mousemove', onMouseMove);
        sliderList.addEventListener('mouseleave', deactivateZoom);
      }

      initShareButton() {
        this.shareButton = this.querySelector('.ec-share-button');
        if (!this.shareButton) return;
        this.shareButton.addEventListener('click', this.onShareButtonClick.bind(this));
      }

      onShareButtonClick() {
        navigator.clipboard.writeText(window.location.href).then(this.announceShareSuccess.bind(this));
      }

      announceShareSuccess() {
        clearTimeout(this.shareButtonTimeout);
        this.shareButton.classList.add('is-copied');
        this.shareButtonTimeout = setTimeout(() => {
          this.shareButton.classList.remove('is-copied');
        }, 2000);

        if (!this.elements.liveRegion) return;
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.textContent = window.accessibilityStrings.shareSuccess;
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      }
    }
  );
}
